import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, MessageCircle } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { QuoteForm } from "./QuoteForm";

const WHATSAPP_URL = "https://wa.me/41782135944";

export const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="border-t border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <p className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand">
            <span className="font-display text-sm tracking-normal text-slate-400">05</span>
            <span className="h-px w-8 bg-sun" aria-hidden="true" />
            Contact
          </p>
          <h2
            data-testid="contact-title"
            className="font-display text-3xl font-normal leading-tight tracking-tight text-ink sm:text-4xl"
          >
            Votre rénovation entre de bonnes mains
          </h2>
          <div className="mt-7 space-y-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            <p>
              Chaque logement et chaque projet sont différents. Chez{" "}
              <strong className="font-semibold text-ink">Zion Solutions Suisse Sàrl</strong>, nous
              étudions vos besoins afin de vous proposer une solution personnalisée, fonctionnelle et
              adaptée à votre mode de vie.
            </p>
            <p>
              Vous souhaitez rénover votre maison ou votre appartement à Genève&nbsp;? Contactez-nous
              pour demander un devis sans engagement.
            </p>
          </div>

          <dl className="mt-10 space-y-5 border-t border-slate-200 pt-8">
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand-light text-brand">
                <Phone className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Téléphone</dt>
                <dd>
                  <a
                    href="tel:+41782135944"
                    data-testid="contact-phone-link"
                    className="text-base font-semibold text-ink transition-colors hover:text-brand sm:text-lg"
                  >
                    +41 78 213 59 44
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand-light text-brand">
                <Mail className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">E-mail</dt>
                <dd>
                  <a
                    href="mailto:zionsolutions.ch@gmail.com"
                    data-testid="contact-email-link"
                    className="break-all text-base font-semibold text-ink transition-colors hover:text-brand sm:text-lg"
                  >
                    zionsolutions.ch@gmail.com
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-brand-light text-brand">
                <MapPin className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Zone d’intervention
                </dt>
                <dd data-testid="contact-zone" className="text-base font-semibold text-ink sm:text-lg">
                  Genève et environs
                </dd>
              </div>
            </div>
          </dl>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={() => scrollToId("devis")}
              data-testid="contact-cta-devis"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-[background-color,color,transform] duration-200 hover:bg-sun hover:text-ink active:scale-[0.98]"
            >
              Demander un devis
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </button>
            <a
              href="tel:+41782135944"
              data-testid="contact-cta-call"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-ink transition-[border-color,color] duration-200 hover:border-brand hover:text-brand"
            >
              <Phone className="h-4 w-4" strokeWidth={1.8} />
              Appeler maintenant
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-sun/60 bg-sun/10 px-6 py-3 text-sm font-semibold text-brand transition-colors duration-200 hover:bg-sun hover:text-ink"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
              Contacter par WhatsApp
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <QuoteForm />
        </motion.div>
      </div>
    </section>
  );
};
